import { useState } from 'react';

import type { Data } from 'components/Form';
import Solana from 'components/Solana';
import useInfo from 'hooks/useInfo';
import useLoadWallet from 'hooks/useLoadWallet';
import useReturnToWalletSettingsTab from 'hooks/useReturnToWalletSettingsTab';
import { DecryptionError } from 'lib/errors';

import WalletIsLocked from './WalletIsLocked';
import type { Props } from './types';

interface Keys {
  public: string;
  private: string;
}

const PrivateKey = ({ onSuccess }: Props) => {
  const loadWallet = useLoadWallet();
  const { dict } = useInfo();
  const [solanaKeys, setSolanaKeys] = useState<Keys | null>(null);
  const [showSolanaPrivateKey, setShowSolanaPrivateKey] =
    useState<boolean>(false);
  const [privateKeysJson, setPrivateKeysJson] = useState<string>('');

  const returnToWalletSettingsTab = useReturnToWalletSettingsTab();

  const close = () => {
    returnToWalletSettingsTab();
    setTimeout(onSuccess, 50);
  };

  const onSubmit = async ({ currentPassword }: Data<string>) => {
    try {
      const wallet = await loadWallet(currentPassword);
      if (wallet) {
        setSolanaKeys({
          public: wallet.solanaWallet.address,
          private: wallet.solanaWallet.privateKey,
        });
        const json = JSON.stringify(
          Array.from(wallet.solanaWallet.privateKeyBytes)
        );
        setPrivateKeysJson(json);
      }
      return undefined;
    } catch (e) {
      if (e instanceof DecryptionError)
        return { currentPassword: dict.passwordIsInvalid };
      throw e;
    }
  };

  if (solanaKeys?.private) {
    return (
      <div className="scroll-dialog wallet-drawer">
        <div className="warning">{dict.solanaPrivateKeyWarning}</div>
        <span className="group-label">
          <Solana />
          {dict.solanaKeysTitle}
        </span>
        <div className="helper">{dict.solanaKeysComingSoon}</div>
        <div className="private-keys">
          <div className="private-key">
            <span className="private-key-label">
              {dict.solanaPublicAddress}
            </span>
            <span className="private-key-value">{solanaKeys?.public}</span>
          </div>
          <div className="private-key">
            <span className="private-key-label">
              {dict.solanaPrivateKey}
              <button
                type="submit"
                className="stroke small icon"
                onClick={() => setShowSolanaPrivateKey(v => !v)}
              >
                {showSolanaPrivateKey ? '🔐' : '🔓'}
              </button>
            </span>
            <span className="private-key-value">
              {showSolanaPrivateKey ? solanaKeys?.private : '*'.repeat(66)}
            </span>
          </div>
        </div>
        <a
          href={`data:application/json;base64,${btoa(encodeURIComponent(privateKeysJson))}`}
          download="solana-keys.json"
          style={{ flexShrink: 0 }}
          className="export-button"
        >
          {dict.exportSolanaPrivateKey}
        </a>

        <button
          onClick={close}
          type="submit"
          className="stroke"
          style={{ flexShrink: 0 }}
        >
          {dict.close}
        </button>
      </div>
    );
  }
  return <WalletIsLocked onSubmit={onSubmit} />;
};
export default PrivateKey;
